import { useState } from 'react';
import { ChevronDown, Loader2 } from 'lucide-react';
import { format } from 'date-fns';
import type { DateRange } from 'react-day-picker';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

interface ImportPreviousTicketsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onImport: (range: DateRange | undefined) => void;
  isImporting?: boolean;
}

export function ImportPreviousTicketsDialog({ open, onOpenChange, onImport, isImporting }: ImportPreviousTicketsDialogProps) {
  const [range, setRange] = useState<DateRange | undefined>();
  const [popoverOpen, setPopoverOpen] = useState(false);

  const label =
    range?.from && range?.to ? `${format(range.from, 'dd MMM, yy')} — ${format(range.to, 'dd MMM, yy')}` : undefined;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='w-[calc(100vw-2rem)] max-w-md p-4'>
        <DialogHeader>
          <DialogTitle className='text-sm font-semibold'>Import previous tickets</DialogTitle>
          <p className='text-xs text-muted-foreground'>Ticket ingestion will start once the support tool is authenticated. It might take a while.</p>
        </DialogHeader>

        <div>
          <p className='text-xs font-medium mb-1.5'>Date Range</p>
          <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
            <PopoverTrigger asChild>
              <button
                type='button'
                className='w-full flex items-center justify-between rounded-md border border-input bg-transparent px-3 h-9 text-sm text-left'
              >
                <span className={label ? '' : 'text-muted-foreground'}>{label ?? 'Enter Date Range'}</span>
                <ChevronDown className='size-4 opacity-50 shrink-0' />
              </button>
            </PopoverTrigger>
            <PopoverContent className='w-auto p-0' align='start'>
              <div className='flex items-center gap-2 p-3 border-b'>
                <Input readOnly value={range?.from ? format(range.from, 'dd MMM, yy') : ''} placeholder='From' className='text-xs h-8' />
                <span className='text-muted-foreground'>—</span>
                <Input readOnly value={range?.to ? format(range.to, 'dd MMM, yy') : ''} placeholder='To' className='text-xs h-8' />
              </div>
              <Calendar mode='range' defaultMonth={range?.from} selected={range} onSelect={setRange} numberOfMonths={2} />
              <div className='flex items-center justify-end gap-2 p-3 border-t'>
                <Button
                  size='sm'
                  className='h-7 text-xs'
                  disabled={!range?.from || !range?.to}
                  onClick={() => setPopoverOpen(false)}
                >
                  Apply
                </Button>
              </div>
            </PopoverContent>
          </Popover>
        </div>

        <div className='flex justify-end mt-2'>
          <Button type='button' className='text-xs' onClick={() => onImport(range)} disabled={isImporting}>
            {isImporting ? <Loader2 className='size-4 animate-spin' /> : null}
            Import
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
